import { type ApiResponse, put } from "@src/appV2/api/api";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { invalidateGetWorkerQuery } from "@src/appV2/Worker/api/useGetWorker";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const updateProfileRequestSchema = z.object({
  profile: z.object({
    email: z.string().email().optional(),
    phone: z.string().optional(),
  }),
});

const updateProfileResponseSchema = z.object({
  success: z.boolean(),
});

export type UpdateProfileRequest = z.infer<typeof updateProfileRequestSchema>;
export type UpdateProfileResponse = z.infer<typeof updateProfileResponseSchema>;
export interface UpdateProfileErrorResponse {
  message?: string;
  error?: string;
}

export const UPDATE_PROFILE_PATH = "/agentProfile/updateProfile";

/**
 * Generic hook to update worker profile (email or phone)
 * Used by both UpdateEmail and UpdatePhone flows
 */
export function useUpdateProfile(
  options: UseMutationOptions<
    ApiResponse<UpdateProfileResponse>,
    AxiosError<UpdateProfileErrorResponse>,
    UpdateProfileRequest
  > = {}
): UseMutationResult<
  ApiResponse<UpdateProfileResponse>,
  AxiosError<UpdateProfileErrorResponse>,
  UpdateProfileRequest
> {
  const queryClient = useQueryClient();

  return useMutation<
    ApiResponse<UpdateProfileResponse>,
    AxiosError<UpdateProfileErrorResponse>,
    UpdateProfileRequest
  >({
    mutationFn: async (requestData) => {
      return await put({
        url: UPDATE_PROFILE_PATH,
        data: requestData,
        requestSchema: updateProfileRequestSchema,
        responseSchema: updateProfileResponseSchema,
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_AGENT_DATA_FAILURE,
    },
    onSuccess: async () => {
      void invalidateGetWorkerQuery(queryClient);
    },
    ...options,
  });
}
